import React from "react";
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import { Button, CardActionArea, CardActions } from '@mui/material';

import "./styles/home.css";

export const Home = ({ darkMode }) => {

  const featuredProjects = [
    {
      title: "Entreno",
      description: "Full-stack AI-powered fitness platform where an LLM agent dynamically generates workout programs, provides coaching, and adapts to user performance. Features encrypted per-user memory, autoregulation, and a conversational coach interface.",
      tech: ["Python", "FastAPI", "PydanticAI", "PostgreSQL", "Docker", "Terraform", "Azure"],
      liveUrl: "https://dev.entreno.io/",
      githubUrl: "https://github.com/hernanpesantez/",
    },
    {
      title: "ML Trading Strategies",
      description: "Evaluated algorithmic trading strategies for JPM stock using machine learning. Built a Manual Strategy with five technical indicators and a Strategy Learner using Bagged Random Trees, comparing in-sample vs. out-of-sample performance.",
      tech: ["Python", "NumPy", "pandas", "scikit-learn", "Matplotlib"],
    },
    {
      title: "AI Search & Game Agents",
      description: "Implemented classic AI algorithms including A* with custom heuristics for map routing, adversarial game-playing agents using minimax with alpha-beta pruning, and Bayesian networks with probabilistic reasoning.",
      tech: ["Python", "pgmpy", "Jupyter", "A*", "Minimax", "Bayesian Networks"],
    },
  ];

  const articles = [
    {
      title: "Agents",
      description: "Anthropic's comprehensive guide to building effective AI agents — covering workflows, orchestration patterns, and when to use autonomous agents vs. simpler solutions.",
      url: "https://www.anthropic.com/engineering/building-effective-agents",
      image: "DALL-E-1.png",
    },
    {
      title: "Emerging Architectures for LLM Applications",
      description: "A deep dive into the emerging tech stack for LLM-powered applications, covering embeddings, vector databases, orchestration frameworks, and retrieval-augmented generation.",
      url: "https://a16z.com/emerging-architectures-for-llm-applications/",
      image: "DALL-E-2.png",
    },
    {
      title: "The Shift to Compound AI Systems",
      description: "Berkeley AI Research on why the frontier of AI is shifting from individual models to compound systems that combine multiple components, retrieval, and tool use.",
      url: "https://bair.berkeley.edu/blog/2024/02/18/compound-ai-systems/",
      image: "DALL-E-3.png",
    },
  ];

  return (

    <Container className={darkMode ? 'container-dark' : 'container-light'} >
      <Box className={darkMode ? 'avatar-box-dark' : 'avatar-box-light'}>
        <Avatar className={darkMode ? 'avatar-dark' : 'avatar-light'}
          alt="Hernan Pesantez"
          src="hernan-avatar-0.png"
        />
      </Box>

      <Box className={darkMode ? 'box-dark' : 'box-light'}>
        <Typography className={`box-about-text`}>
        Hi, I'm Hernan, a Senior Consultant at Avanade with 6+ years building production back-end systems, microservices, and cloud-native platforms across Azure and AWS. My work spans the full stack of modern software, from Python/FastAPI APIs and PySpark data pipelines to CI/CD, infrastructure-as-code, and container orchestration.

        <br></br>
        <br></br>

        I'm deeply interested in where software engineering meets AI. I build with agentic AI frameworks, LLM-powered tools, and applied ML — and I'm currently pursuing an M.S. in Computer Science with a Machine Learning specialization at Georgia Tech to go deeper. Projects like Entreno, where an AI coach generates and adapts workout programs through conversation, reflect where I'm headed: intelligent systems that solve real problems.

        <br></br>
        <br></br>

        Below you'll find some of my recent work and resources I find valuable. Feel free to explore and reach out.

        </Typography>
      </Box>


      <Divider className={darkMode ? 'divider-dark' : 'divider-light'} />

      <Typography className={`box-title-text`}>
        FEATURED PROJECTS
      </Typography>

      <Box className={darkMode ? 'box-project-dark' : 'box-project-light'}>
        {featuredProjects.map((project, index) => (
          <Card key={index} className={darkMode ? 'card-project-dark' : 'card-project-light'}>
            <CardActionArea
              href={project.liveUrl || project.githubUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="card-action-area"
            >
              <CardContent className="card-content-featured">
                <Typography gutterBottom variant="h5" component="div" className="project-card-title">
                  {project.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" className={darkMode ? 'card-text-dark' : ''}>
                  {project.description}
                </Typography>
                <Box className="tech-chips" sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {project.tech.map((t, i) => (
                    <Chip
                      key={i}
                      label={t}
                      size="small"
                      className={darkMode ? 'chip-dark' : 'chip-light'}
                    />
                  ))}
                </Box>
              </CardContent>
            </CardActionArea>
            <CardActions>
              {project.liveUrl && (
                <Link href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon className={darkMode ? "icon-dark" : "icon-light"} />
                </Link>
              )}
              {project.githubUrl && (
                <Link href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                  <GitHubIcon className={darkMode ? "icon-dark" : "icon-light"} />
                </Link>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      <Divider className={darkMode ? 'divider-dark' : 'divider-light'} />

      <Typography className={`box-title-text`}>
        READING
      </Typography>

      <Box className={darkMode ? 'box-project-dark' : 'box-project-light'}>
        {articles.map((article, index) => (
          <Card key={index} className={darkMode ? 'card-project-dark' : 'card-project-light'}>
            <CardActionArea href={article.url} target="_blank" rel="noopener noreferrer">
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  {article.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" className={darkMode ? 'card-text-dark' : ''}>
                  {article.description}
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions>
              <Button size="small" color="primary" href={article.url} target="_blank" rel="noopener noreferrer">
                READ
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

    </Container>

  );
};
